/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { Button, Stack, TextField, Typography } from '@mui/material'
import React from 'react'
import useIsMounted from 'src/hooks/useIsMounted'
import InfiniteAutocomplete from 'src/components/IAM/InfiniteAutocomplete'
import { useAdminModule } from 'src/hooks/IAM/AdminModule/useAdminModule'
import TableModules from './Components/TableModules'
import { usePagination } from 'src/hooks/IAM/Components/Pagination/usePagination'
import BoxWithShadow from 'src/components/BoxWithShadow'
/**
 * Componente principal `AdminModule` utilizado para crear y gestionar módulos.
 * Contiene un formulario para crear nuevos módulos y una tabla que muestra
 * todos los módulos existentes. Utiliza varias funciones de carga y
 * creación de módulos desde los hooks correspondientes.
 * Efecto utilizado para cargar los módulos cuando el componente está montado.
  * Llama a las funciones `loadModules` y `loadModulesWithoutFormat` para
  * cargar los módulos con y sin formato.
  *
  * @example
  * ```tsx
  * useEffect(() => {
  *   if (!isMounted) return;
  * 
  *   loadModules({
  *     currentPage: 1,
  *     pageSize: 10,
  *   });
  *   loadModulesWithoutFormat({
  *     currentPage: 1,
  *     pageSize: 10,
  *   });
  * }, [isMounted]);
  * ```
  * 
  * @throws {Error} Lanza un error si el componente no está montado.
  * @example
  * ```tsx
  * // Renderizar el componente AdminModule en una aplicación React
  * import AdminModule from './AdminModule'
  * 
  * function App() {
  *   return (
  *     <div>
  *       <AdminModule />
  *     </div>
  *   )
  * }
  * export default App
  * 
  * ```
  *
  * @returns {JSX.Element} El componente JSX que contiene el formulario y la tabla de módulos.
  * 
  * @component
*/
const AdminModule = (): JSX.Element => {
  const {
    modules,                      // Lista de módulos disponibles.
    selectedModules,              // Módulos seleccionados por el usuario.
    loadModules,                  // Función para cargar módulos con formato.
    loadModulesWithoutFormat,      // Función para cargar módulos sin formato.
    createModuleAdmin,             // Función para crear un nuevo módulo.
  } = useAdminModule();

  const { currentPage, rowsPerPage } = usePagination(); // Variables y funciones de paginación.
  const isMounted = useIsMounted(); // Hook para verificar si el componente está montado.
  const [nameModule, setNameModule] = useState<string>(''); // Estado para el nombre del nuevo módulo.

  /**

   */
  useEffect(() => {
    if (!isMounted) return;

    loadModules({
      currentPage,
      pageSize: rowsPerPage,
    });
    loadModulesWithoutFormat({
      currentPage,
      pageSize: rowsPerPage,
    });
  }, [isMounted, currentPage, rowsPerPage]); // Asegúrate de incluir dependencias.

  /**
   * Función que maneja el envío del formulario para crear un nuevo módulo.
   * 
   * @param {React.FormEvent<HTMLFormElement>} e - El evento del formulario.
   * @returns {void}
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    createModuleAdmin({
      path: selectedModules[selectedModules.length - 1]?.path ?? '',
      keyName: nameModule,
    });
  };

  /**
   * Renderiza el formulario para crear un nuevo módulo y una tabla que muestra
   * todos los módulos. Incluye un campo de texto para introducir el nombre del módulo
   * y un autocompletado infinito para seleccionar la carpeta raíz.
   *
   * @example
   * ```tsx
   * return (
   *   <AdminModule />
   * );
   * ```
   */
  return (
    <BoxWithShadow sx={{ padding: '20px' }}>
      {/* Título de la sección */}
      <Typography variant="h4" gutterBottom>
        Crear modulo
      </Typography>

      {/* Formulario para la creación de módulos */}
      <form onSubmit={handleSubmit}>
        <Stack direction="row" gap={2} mb={2}>
          {/* Autocompletado infinito para seleccionar la carpeta raíz */}
          <InfiniteAutocomplete
            data={{
              keyName: 'Carpeta raiz',
              moduleId: 0,
              path: '',
              subModules: modules,
            }}
          />
          {/* Campo de texto para el nombre del módulo */}
          <TextField
            id="outlined-basic"
            label="Nombre del modulo"
            variant="outlined"
            value={nameModule}
            required
            onChange={(e) => setNameModule(e.target.value)}
            sx={{ ml: -2 }}
          />
        </Stack>

        <Button variant="contained" color="primary" type="submit">
          Crear
        </Button>
      </form>

      {/* Barra de búsqueda y título de la tabla de módulos */}
      <Stack direction={'row'} justifyContent={'space-between'} sx={{ marginBlock: '20px' }}>
        <h2>Todos los modulos</h2>
        <TextField
          id="outlined-basic"
          label="Buscar modulo"
          variant="outlined"
        />
      </Stack>

      {/* Tabla que muestra los módulos */}
      <TableModules />
    </BoxWithShadow>
  );
}

export default AdminModule;
